import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { templateAPI } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ArrowLeft, FileText, Upload } from 'lucide-react';
import { toast } from 'sonner';

const Templates = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const { data } = await templateAPI.getAll();
      setTemplates(data);
    } catch (error) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.match(/\.(docx|pdf)$/i)) {
      toast.error('Please upload a DOCX or PDF file');
      return;
    }

    setUploading(true);
    try {
      const { data } = await templateAPI.upload(file);
      setTemplates([data, ...templates]);
      toast.success('Custom template uploaded successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload template');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFCFB]">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-stone-200/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button data-testid="back-button" variant="ghost" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-2xl font-bold text-[#1C1917]">Templates</h1>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-8 flex justify-between items-start">
          <div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight text-[#1C1917] mb-2">Choose a Template</h2>
            <p className="text-base text-stone-800 leading-relaxed">Start with a pre-built format or upload your own</p>
          </div>
          <div>
            <input
              ref={fileInputRef}
              data-testid="template-upload-input"
              type="file"
              accept=".docx,.pdf"
              onChange={handleUpload}
              className="hidden"
            />
            <Button
              data-testid="upload-template-button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="bg-[#C53030] hover:bg-[#9B2C2C] text-white rounded-md"
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploading ? 'Uploading...' : 'Upload Custom Format'}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C53030] mx-auto mb-4"></div>
            <p className="text-stone-600">Loading templates...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {templates.map((template) => (
              <Card
                key={template.template_id}
                data-testid={`template-card-${template.template_id}`}
                className="p-6 border border-stone-200 hover:-translate-y-1 transition-transform duration-200 ease-out cursor-pointer"
                onClick={() => navigate('/builder', { state: { templateId: template.template_id } })}
              >
                <FileText className="w-10 h-10 text-[#C53030] mb-4" />
                <h3 className="text-xl sm:text-2xl text-[#1C1917] mb-2">{template.name}</h3>
                <p className="text-base text-stone-800 leading-relaxed mb-4">{template.description}</p>
                <div className="text-sm text-stone-600">
                  <span className="inline-block px-2 py-1 bg-stone-100 rounded text-xs">
                    {template.structure.days} Days
                  </span>
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Templates;